/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  RefreshCw,
  AlertTriangle,
  Lock,
  Users,
  Wallet,
  Coins,
  Activity,
  Mail,
  Link2,
} from "lucide-react";
import DataSourcesBanner from "@/components/admin/DataSourcesBanner";

/**
 * AP-5D — Read-only AssosPay operations dashboard.
 *
 * Single network call: `GET /api/admin/assospay/summary[?refresh=1]`.
 * Same `arc_admin_token` bearer pattern as AdminVesting. NO writes,
 * NO RPC, NO wallet signature, NO env flip. NEVER renders raw emails,
 * full IUIDs, full wallet addresses, magic-link tokens, or JWTs.
 */
const TOKEN_KEY = "arc_admin_token";

async function authedFetch(path, token, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${text || res.statusText}`);
  }
  return res.json();
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

export default function AdminAssosPay() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || "",
  );
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async (tk, opts = {}) => {
    const force = !!opts.refresh;
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const path = force
        ? "/admin/assospay/summary?refresh=1"
        : "/admin/assospay/summary";
      const out = await authedFetch(path, tk);
      setSummary(out);
    } catch (e) {
      const msg = String(e.message || "");
      if (msg.startsWith("401") || msg.startsWith("403")) {
        toast.error(`auth rejected: ${msg}`);
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        toast.error(`load failed: ${msg}`);
      }
    } finally {
      if (force) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  const inFlightRef = React.useRef(false);

  useEffect(() => {
    if (!authed || !token) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    fetchSummary(token).finally(() => {
      inFlightRef.current = false;
    });
  }, [authed, token, fetchSummary]);

  const onLogin = async () => {
    if (!token) {
      toast.error("token required");
      return;
    }
    try {
      await authedFetch("/admin/assospay/summary", token);
    } catch (e) {
      toast.error(`auth failed: ${e.message}`);
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
    setAuthed(true);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setSummary(null);
  };

  if (!authed) {
    return <LoginShell token={token} setToken={setToken} onLogin={onLogin} />;
  }

  return (
    <div
      className="min-h-screen bg-[#050505] text-white"
      data-testid="admin-assospay-page"
    >
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Lock className="h-4 w-4 text-[#00F0FF]" />
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
              AssosPay · Operator
            </p>
            <h1 className="font-display text-xl font-black truncate">
              AssosPay Admin
            </h1>
          </div>
          <button
            type="button"
            onClick={() => fetchSummary(token, { refresh: true })}
            disabled={loading || refreshing}
            data-testid="admin-assospay-refresh-btn"
            className="inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "refreshing…" : "Refresh"}
          </button>
          <Link
            to="/admin/assospay/reports"
            data-testid="admin-assospay-reports-link"
            className="inline-flex items-center gap-2 rounded border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 hover:bg-white/10 transition-colors"
          >
            Reports
          </Link>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-assospay-logout-btn"
            className="inline-flex items-center gap-2 rounded border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/65 hover:bg-white/10 transition-colors"
          >
            Disconnect
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <HardRuleBanner />
        <DataSourcesBanner />
        {loading && !summary && (
          <div data-testid="admin-assospay-loading" className="font-mono text-xs text-white/55">
            Loading summary…
          </div>
        )}
        {summary && (
          <>
            <SafetyFlagsStrip summary={summary} />
            <UsersGrid users={summary.users} />
            <PortfolioGrid portfolio={summary.arc_portfolio} />
            <RedemptionsSection redemptions={summary.redemptions} />
            <WalletsTable wallets={summary.wallets} />
            <RecentActivityTable activity={summary.recent_activity} />
            <MetaFooter summary={summary} />
          </>
        )}
      </main>
    </div>
  );
}

/* ------------------------ login gate ------------------------ */
function LoginShell({ token, setToken, onLogin }) {
  return (
    <div
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4"
      data-testid="admin-assospay-login-shell"
    >
      <div className="w-full max-w-md rounded-md border border-white/10 bg-black/60 p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
          AssosPay · Operator
        </p>
        <h1 className="font-display text-xl font-black mt-1 mb-4">
          AssosPay Admin · Sign in
        </h1>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">
          ARC_ADMIN_TOKEN
        </label>
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="paste your admin token"
          data-testid="admin-assospay-token-input"
          className="mt-2 w-full rounded border border-white/15 bg-black/40 px-3 py-2 font-mono text-xs text-white outline-none focus:border-[#00F0FF]/60"
        />
        <button
          type="button"
          onClick={onLogin}
          data-testid="admin-assospay-login-btn"
          className="mt-4 inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors"
        >
          <Lock className="h-3 w-3" />
          Enter
        </button>
        <p className="mt-4 font-mono text-[10px] text-white/40">
          Read-only operator view. No writes, no claim, no transfer, no signature.
        </p>
      </div>
    </div>
  );
}

/* ------------------------ banners + strips ------------------------ */
function HardRuleBanner() {
  return (
    <div
      data-testid="admin-assospay-hard-rule"
      className="rounded-md border border-[#39FF14]/30 bg-[#39FF14]/5 p-4 flex items-start gap-2"
    >
      <ShieldCheck className="h-4 w-4 text-[#39FF14] mt-0.5" />
      <div className="font-mono text-[11px] text-white/70 leading-relaxed">
        <span className="text-[#39FF14] font-bold">Read-only operator view.</span>{" "}
        No writes, no token transfer, no balance mutation, no blockchain
        transaction, no wallet signature. Emails, IUIDs, and full wallet
        addresses are NEVER displayed.
      </div>
    </div>
  );
}

function SafetyFlagsStrip({ summary }) {
  const shadow = summary.shadow_mode;
  const webhookOn = summary.webhook_enabled;
  const pilot = summary.pilot_mode || "unknown";
  return (
    <div
      data-testid="admin-assospay-safety-flags"
      data-shadow-mode={shadow ? "true" : "false"}
      data-webhook-enabled={webhookOn ? "true" : "false"}
      data-pilot-mode={pilot}
      className="flex flex-wrap items-center gap-2"
    >
      <Pill
        kind={shadow ? "cyan" : "green"}
        label={shadow ? "Pre-launch shadow mode" : "Live contract"}
        testid="flag-shadow-mode"
      />
      <Pill
        kind={webhookOn ? "amber" : "muted"}
        label={webhookOn ? "Webhook ENABLED" : "Webhook OFF"}
        testid="flag-webhook"
      />
      <Pill
        kind="muted"
        label={`Pilot: ${pilot}`}
        testid="flag-pilot-mode"
      />
    </div>
  );
}

function Pill({ kind, label, testid }) {
  const palette = {
    cyan: "border-[#00F0FF]/40 bg-[#00F0FF]/5 text-[#00F0FF]",
    green: "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]",
    amber: "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]",
    muted: "border-white/15 bg-white/5 text-white/65",
  }[kind || "muted"];
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${palette}`}
    >
      {label}
    </span>
  );
}

/* ------------------------ users ------------------------ */
function UsersGrid({ users }) {
  return (
    <Section title="Users" icon={Users} testid="section-users">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile testid="users-total" label="Total accounts" value={users.total} accent="cyan" />
        <Tile testid="users-native" label="Native email" value={users.native_email} icon={Mail} accent="cyan" />
        <Tile testid="users-assos365" label="Assos365 linked" value={users.assos365_linked} icon={Link2} accent="green" />
        <Tile testid="users-with-wallet" label="Saved wallet" value={users.with_saved_wallet} icon={Wallet} />
        <Tile testid="users-declared" label="Declared wallet" value={users.wallet_declared} accent="amber" />
        <Tile testid="users-verified" label="Verified wallet" value={users.wallet_verified} accent={users.wallet_verified > 0 ? "green" : "muted"} />
      </div>
    </Section>
  );
}

/* ------------------------ portfolio ------------------------ */
function PortfolioGrid({ portfolio }) {
  const chainsRaw = portfolio.wallet_distinct_chains;
  const chainsLabel = Array.isArray(chainsRaw) ? chainsRaw.join(", ") : "not available";
  return (
    <Section title="ARC Portfolio" icon={Coins} testid="section-portfolio">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          testid="portfolio-on-chain-arc"
          label="On-chain ARC total"
          value={portfolio.on_chain_total_arc || "0"}
          hint={portfolio.shadow_mode ? "Shadow mode — contract not deployed" : null}
          accent={portfolio.shadow_mode ? "muted" : "green"}
        />
        <Tile
          testid="portfolio-bindings"
          label="Wallet bindings"
          value={portfolio.wallet_bindings}
        />
        <Tile
          testid="portfolio-unique-addrs"
          label="Unique addresses"
          value={portfolio.unique_wallet_addresses}
        />
        <Tile
          testid="portfolio-chains"
          label="Distinct chains"
          value={chainsLabel}
          accent="muted"
        />
      </div>
    </Section>
  );
}

/* ------------------------ redemptions ------------------------ */
function RedemptionsSection({ redemptions }) {
  return (
    <Section title="Redemptions" icon={Activity} testid="section-redemptions">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Tile testid="redemp-total" label="Total" value={redemptions.total} />
        <Tile testid="redemp-pending" label="Pending" value={redemptions.pending} accent="amber" />
        <Tile testid="redemp-fulfilled" label="Fulfilled" value={redemptions.fulfilled} accent="green" />
        <Tile testid="redemp-rejected" label="Rejected" value={redemptions.rejected} accent="muted" />
        <Tile testid="redemp-failed" label="Failed" value={redemptions.failed} accent="amber" />
        <Tile testid="redemp-eur-total" label="EUR credited" value={`€${redemptions.eur_credited_total || "0"}`} accent="cyan" />
      </div>
      <Table
        testid="redemp-recent-table"
        cols={["Ref", "User", "Status", "ARC", "EUR", "Created"]}
        rows={(redemptions.recent || []).map((r) => [
          <span className="font-mono text-[10px] text-white/80">{r.arc_reference_id?.slice(0, 18) || "—"}…</span>,
          r.arc_user_id_last4 ? `…${r.arc_user_id_last4}` : "—",
          <StatusChip status={r.status} />,
          r.arc_amount || "—",
          r.eur_amount || "—",
          formatTs(r.created_at),
        ])}
        emptyLabel="No redemptions recorded."
      />
    </Section>
  );
}

function StatusChip({ status }) {
  const palette = {
    fulfilled: "text-[#39FF14] border-[#39FF14]/40",
    submitted: "text-[#FFB800] border-[#FFB800]/40",
    pending_partner_review: "text-[#FFB800] border-[#FFB800]/40",
    rejected: "text-white/55 border-white/20",
    failed_terminal: "text-[#FF3B30] border-[#FF3B30]/40",
    failed_retryable: "text-[#FFB800] border-[#FFB800]/40",
  }[status] || "text-white/55 border-white/15";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${palette}`}>
      {status || "—"}
    </span>
  );
}

/* ------------------------ wallets ------------------------ */
function WalletsTable({ wallets }) {
  return (
    <Section
      title={`Wallet bindings (${wallets.row_count_total})`}
      icon={Wallet}
      testid="section-wallets"
    >
      <Table
        testid="wallets-table"
        cols={["User", "Auth", "Address", "Verification", "Chain", "Updated"]}
        rows={(wallets.rows || []).map((r) => [
          r.arc_user_id_last4 ? `…${r.arc_user_id_last4}` : "—",
          (r.auth_methods || []).join(", ") || "—",
          <span className="font-mono text-[10px] text-white/80">{r.wallet_address_redacted}</span>,
          <VerificationChip status={r.verification_status} />,
          r.chain_id ?? "—",
          formatTs(r.updated_at),
        ])}
        emptyLabel="No wallet bindings yet."
      />
    </Section>
  );
}

function VerificationChip({ status }) {
  if (status === "verified") {
    return (
      <span className="inline-flex items-center rounded border border-[#39FF14]/40 bg-[#39FF14]/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#39FF14]">
        verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded border border-[#FFB800]/40 bg-[#FFB800]/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#FFB800]">
      declared
    </span>
  );
}

/* ------------------------ activity ------------------------ */
function RecentActivityTable({ activity }) {
  return (
    <Section
      title="Recent account activity"
      icon={Activity}
      testid="section-activity"
    >
      <Table
        testid="activity-table"
        cols={["Event", "Outcome", "User", "Token ID", "When"]}
        rows={(activity.rows || []).map((r) => [
          <span className="font-mono text-[10px] text-white/80">{r.event_type || "—"}</span>,
          <OutcomeChip outcome={r.outcome} />,
          r.arc_user_id_last4 ? `…${r.arc_user_id_last4}` : "—",
          r.token_id ? <span className="font-mono text-[10px] text-white/60">{r.token_id}…</span> : "—",
          formatTs(r.ts),
        ])}
        emptyLabel="No audit rows."
      />
    </Section>
  );
}

function OutcomeChip({ outcome }) {
  if (outcome === "success") {
    return <span className="text-[#39FF14] font-mono text-[10px]">success</span>;
  }
  if (outcome === "failure") {
    return <span className="text-[#FF3B30] font-mono text-[10px]">failure</span>;
  }
  return <span className="text-white/45 font-mono text-[10px]">{outcome || "—"}</span>;
}

/* ------------------------ shared primitives ------------------------ */
function Section({ title, icon: Icon, testid, children }) {
  return (
    <section data-testid={testid} className="rounded-md border border-white/5 bg-black/40 p-4">
      <h2 className="flex items-center gap-2 font-display text-xs uppercase tracking-[0.25em] text-white/80 mb-3">
        <Icon className="h-3.5 w-3.5 text-[#00F0FF]" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function Tile({ label, value, hint, accent, icon: Icon, testid }) {
  const palette = {
    cyan: "text-[#00F0FF]",
    green: "text-[#39FF14]",
    amber: "text-[#FFB800]",
    muted: "text-white/75",
  }[accent || "muted"];
  return (
    <div
      data-testid={testid}
      className="rounded border border-white/10 bg-black/40 p-3"
    >
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-white/45">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {label}
      </div>
      <div className={`mt-1 font-display text-lg font-black ${palette}`}>
        {value ?? "—"}
      </div>
      {hint && (
        <div className="mt-1 font-mono text-[9px] text-white/40">{hint}</div>
      )}
    </div>
  );
}

function Table({ cols, rows, emptyLabel, testid }) {
  if (!rows || rows.length === 0) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="font-mono text-[11px] text-white/45 italic"
      >
        {emptyLabel || "No data."}
      </div>
    );
  }
  return (
    <div data-testid={testid} className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-white/5">
            {cols.map((c, i) => (
              <th
                key={i}
                className="px-3 py-2 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 font-mono text-[11px] text-white/80">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetaFooter({ summary }) {
  return (
    <div
      data-testid="admin-assospay-meta-footer"
      className="rounded-md border border-white/5 bg-black/40 p-4 flex flex-wrap items-center gap-4 font-mono text-[10px] text-white/40"
    >
      <span>
        Generated:{" "}
        <span data-testid="admin-assospay-generated-at" className="text-white/70">
          {formatTs(summary.generated_at)}
        </span>
      </span>
      <span className="ml-auto inline-flex items-center gap-1 text-white/30">
        <AlertTriangle className="h-3 w-3" />
        no writes · no signature · no rpc
      </span>
    </div>
  );
}
